<template>
  <el-dialog
    :visible.sync="dialogVisible"
    title="查看历史版本"
    width="1180px"
    :close-on-click-modal="false"
  >
    <div v-if="historyRow" class="history-shell">
      <div class="history-summary">
        <div class="summary-item">
          <span>平台组</span>
          <strong>{{ historyRow.groupName || '-' }}</strong>
        </div>
        <div class="summary-item">
          <span>当前版本</span>
          <strong>{{ activeVersion.versionNo || historyRow.currentVersion || '-' }}</strong>
        </div>
        <div class="summary-item">
          <span>已发布版本</span>
          <strong>{{ publishedVersionCount }} 个</strong>
        </div>
        <div class="summary-item">
          <span>查看方式</span>
          <strong>只读快照</strong>
        </div>
      </div>

      <div class="mini-title">已发布版本列表</div>
      <div class="table-wrap">
        <el-table
          :data="publishedVersionRows"
          border
          size="mini"
          :header-cell-style="{ backgroundColor: '#F4F8FB', textAlign: 'center' }"
          :cell-style="{ textAlign: 'center' }"
        >
          <el-table-column prop="versionNo" label="规则版本" min-width="140" />
          <el-table-column prop="publishedTime" label="发布时间" min-width="170" />
          <el-table-column label="操作" min-width="120">
            <template slot-scope="{ row }">
              <el-button type="text" @click="showSnapshot(row)">查看快照</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <template v-if="hasSelectedSnapshot">
        <div class="mini-title">版本快照：{{ activeVersion.versionNo || '-' }}</div>

        <div class="snapshot-shell">
          <nav class="snapshot-nav">
            <button
              v-for="(category, index) in snapshotCategoryList"
              :key="category.categoryId"
              type="button"
              :class="{ active: activeCategoryId === category.categoryId }"
              @click="activeCategoryId = category.categoryId"
            >
              <span>{{ `${index + 1}. ${category.categoryName}` }}</span>
              <i :class="getCategoryStatus(category).statusClass">{{ getCategoryStatus(category).statusText }}</i>
            </button>
          </nav>

          <div v-if="activeCategory" class="snapshot-content">
            <div class="snapshot-content-head">
              <div>
                <h3>{{ activeCategory.categoryName }}</h3>
                <p>历史版本快照，仅供查看</p>
              </div>
              <span>{{ (activeCategory.fields || []).length }} 个配置项</span>
            </div>

            <div class="snapshot-field-grid">
              <div
                v-for="field in activeCategory.fields || []"
                :key="field.fieldId"
                :class="['snapshot-field', { wide: field.controlType === 'MULTI_TEXT' }]"
              >
                <span class="snapshot-field-label">
                  {{ field.fieldName }}
                  <b v-if="field.sourceRequired" class="required-badge">* 必填</b>
                </span>
                <span class="snapshot-field-value" :title="resolveFieldDisplayValue(field)">
                  {{ resolveFieldDisplayValue(field) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div slot="footer">
      <el-button @click="dialogVisible = false">关闭</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { parseSnapshot, resolveFieldDisplayValue, sortBySortOrder } from '../platformRuleUtils'

export default {
  name: 'RuleHistoryDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    historyRow: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      versionRows: [],
      activeVersionNo: '',
      activeCategoryId: '',
      snapshotCategories: []
    }
  },
  computed: {
    dialogVisible: {
      get () {
        return this.visible
      },
      set (val) {
        this.$emit('update:visible', val)
      }
    },
    publishedVersionRows () {
      return this.versionRows
    },
    publishedVersionCount () {
      return this.publishedVersionRows.length
    },
    hasSelectedSnapshot () {
      return !!this.activeVersionNo
    },
    activeVersion () {
      if (!this.activeVersionNo) return {}
      return this.publishedVersionRows.find(item => item.versionNo === this.activeVersionNo) || {}
    },
    snapshotCategoryList () {
      return this.snapshotCategories
    },
    activeCategory () {
      return this.snapshotCategoryList.find(item => item.categoryId === this.activeCategoryId) || this.snapshotCategoryList[0]
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler (val) {
        if (val) {
          this.initDialogData()
          return
        }
        this.versionRows = []
        this.activeVersionNo = ''
        this.activeCategoryId = ''
        this.snapshotCategories = []
      }
    }
  },
  methods: {
    getCategoryStatus (category) {
      const fieldList = category.fields || []
      const requiredFieldCount = fieldList.filter(item => item.sourceRequired).length
      const completedFieldCount = fieldList.filter(item => resolveFieldDisplayValue(item) !== '-').length
      let statusText = '待配置'
      let statusClass = 'status-warn'
      if (requiredFieldCount > 0 && completedFieldCount >= requiredFieldCount) {
        statusText = '已完成'
        statusClass = 'status-ok'
      } else if (completedFieldCount > 0) {
        statusText = '缺必要项'
        statusClass = 'status-danger'
      }
      return {
        statusText,
        statusClass
      }
    },
    async initDialogData () {
      if (!this.historyRow) {
        return
      }
      const res = await this.$q.newProduct.platformImageRuleVersions({
        params: {
          groupId: this.historyRow.groupId
        }
      })
      this.versionRows = res.data || []
    },
    async showSnapshot (row) {
      const res = await this.$q.newProduct.platformImageRuleVersionDetail({
        params: {
          versionId: row.id
        }
      })
      const snapshotData = parseSnapshot(res.data && res.data.snapshot)
      this.activeVersionNo = row.versionNo || ''
      this.snapshotCategories = sortBySortOrder(snapshotData.categories || []).map(category => ({
        ...category,
        fields: sortBySortOrder(category.fields || [])
      }))
      this.activeCategoryId = this.snapshotCategories.length ? this.snapshotCategories[0].categoryId : ''
    },
    resolveFieldDisplayValue
  }
}
</script>

<style lang="stylus" scoped>
.history-shell
  max-height 70vh
  overflow auto

.history-summary
  display grid
  grid-template-columns repeat(4, minmax(0, 1fr))
  gap 12px
  margin-bottom 14px

.summary-item
  padding 12px
  background #f7f8fa
  border-radius 6px
  display flex
  flex-direction column
  gap 6px

.summary-item span
  color #909399
  font-size 12px

.summary-item strong
  color #303133
  font-size 16px

.mini-title
  font-size 14px
  font-weight 600
  color #303133
  margin 14px 0 10px

.snapshot-shell
  display flex
  gap 14px

.snapshot-nav
  width 300px
  flex-shrink 0
  display flex
  flex-direction column
  gap 8px

.snapshot-nav button
  display flex
  justify-content space-between
  align-items center
  width 100%
  border 1px solid #ebeef5
  border-radius 6px
  background #fff
  padding 10px 12px
  text-align left
  cursor pointer

.snapshot-nav button.active
  border-color #409eff
  background #f4f8ff

.snapshot-nav i
  font-style normal
  flex-shrink 0
  margin-left 8px

.snapshot-content
  flex 1
  min-width 0
  border 1px solid #ebeef5
  border-radius 6px
  padding 14px
  background #fff

.snapshot-content-head
  display flex
  justify-content space-between
  gap 12px
  margin-bottom 14px

.snapshot-content-head h3
  margin 0 0 6px
  font-size 16px
  color #303133

.snapshot-content-head p
  margin 0
  color #909399
  font-size 12px

.snapshot-field-grid
  display grid
  grid-template-columns repeat(2, minmax(0, 1fr))
  gap 12px

.snapshot-field
  padding 12px
  border 1px solid #ebeef5
  border-radius 6px
  background #fafafa
  display flex
  flex-direction column
  gap 8px

.snapshot-field.wide
  grid-column 1 / -1

.snapshot-field-label
  color #606266
  font-weight 600

.snapshot-field-value
  color #303133
  word-break break-all

.required-badge
  color #f56c6c
  margin-left 6px

.status-ok
  color #67c23a

.status-warn
  color #e6a23c

.status-danger
  color #f56c6c

@media (max-width: 1200px)
  .history-summary
    grid-template-columns repeat(2, minmax(0, 1fr))
  .snapshot-shell
    flex-direction column
  .snapshot-nav
    width 100%
  .snapshot-field-grid
    grid-template-columns 1fr
</style>
